import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Badge — rc58 "Linear/Terminal" refresh.
 *
 * Cambio respecto a rc≤57:
 *   · Antes: pill rounded-full con bg + text + border (estilo shadcn default).
 *   · Ahora: dot circular + texto JetBrains Mono uppercase tracking-wider,
 *     sin fondo de pill. Mucho más limpio, más "técnico", consistente con el
 *     mockup Onyx Electric. Para enfatizar (status critical) se usa la variante
 *     `solid` que sí pinta fondo suave.
 *
 * Compatibilidad: las variantes existentes (`success`, `warning`, `destructive`,
 * `default`, `secondary`, `outline`) siguen funcionando — solo cambia el look.
 * Componentes que dependen de la estructura `<span class="…">{text}</span>`
 * mantienen el contrato exacto.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-wider transition-colors",
  {
    variants: {
      variant: {
        default:     "text-electric",
        secondary:   "text-muted-foreground",
        destructive: "text-danger",
        success:     "text-success",
        warning:     "text-warn",
        outline:     "rounded-md border border-border px-2 py-0.5 text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  }
);

// Mapa de "dot color" por variant — usado solo si `dot !== false`.
const DOT_BG: Record<string, string> = {
  default:     "bg-electric",
  secondary:   "bg-muted-foreground",
  destructive: "bg-danger",
  success:     "bg-success",
  warning:     "bg-warn",
  outline:     "bg-muted-foreground",
};

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Pone el dot al inicio. Por default true. Pasa `dot={false}` para texto puro. */
  dot?: boolean;
}

export function Badge({ className, variant, dot = true, children, ...props }: BadgeProps) {
  const v = variant ?? "default";
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn("inline-block h-1.5 w-1.5 rounded-full shrink-0", DOT_BG[v])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
